import { DatabaseType, MetaDataInterface, ModelInterface, StorageType, TaskInterface } from "@/interfaces";
import { XYPosition } from "@xyflow/react";
import { ModelNodeInterface } from "@/interfaces/uiNodeInterfaces/ModelNodeInterface";
import { generateConstellationSchema } from "@/utils/flow/constellationSchemaAlgo";
import { getConstellationPositions } from "@/utils/flow/getConstellationPositions";
import  PROFESSIONAL_BACKGROUND_COLORS  from "@/constants/ProfessionalColors";
import { AllModels } from "@/data/TemplateModels";


export const getThemeColor = (index: number): string => {
  return PROFESSIONAL_BACKGROUND_COLORS[
    Math.floor(Math.random() * PROFESSIONAL_BACKGROUND_COLORS.length)
  ];
};

// Geometry helpers
export const calculateDistance = (a: XYPosition, b: XYPosition): number => {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
};

export const applyNodeRepulsion = (
  modelNodes: ModelNodeInterface[],
  minDistance = 250
): ModelNodeInterface[] => {
  const newModelNodes = modelNodes.map((node) => ({ ...node }));
  for (let i = 0; i < newModelNodes.length; i++) {
    for (let j = i + 1; j < newModelNodes.length; j++) {
      const a = newModelNodes[i];
      const b = newModelNodes[j];
      if (!a.position || !b.position) continue;
      const dist = calculateDistance(a.position, b.position);
      if (dist < minDistance && dist > 0) {
        const dx = b.position.x - a.position.x;
        const dy = b.position.y - a.position.y;
        const angle = Math.atan2(dy, dx);
        const move = (minDistance - dist) / 2;
        a.position.x -= Math.cos(angle) * move;
        a.position.y -= Math.sin(angle) * move;
        b.position.x += Math.cos(angle) * move;
        b.position.y += Math.sin(angle) * move;
      }
    }
  }
  return newModelNodes;
};

// Storage utils (browser only)
export const StorageUtils = {
  save: (key: string, data: any) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(key, JSON.stringify(data));
    }
  },
  remove: (key: string) => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(key);
    }
  },
  saveExtractedTasks: (tasks: TaskInterface[]) =>
    StorageUtils.save("extractedTasks", tasks),
  saveExtractedModels: (models: ModelInterface[]) =>
    StorageUtils.save("extractedModels", models),
  saveSelectedTasks: (tasks: Set<string>) =>
    StorageUtils.save("selectedTasks", [...tasks]),
  saveExtractionError: (error: string | null) => {
    if (error) {
      StorageUtils.save("extractionError", error);
    } else {
      StorageUtils.remove("extractionError");
    }
  },
};

// Signature builder to detect meaningful state changes
export const buildStateSignature = (state: {
  models?: ModelInterface[];
  tasks?: TaskInterface[];
  metadata?: MetaDataInterface;
}): string => {
  const models = state.models || [];
  const tasks = state.tasks || [];
  const metadata = state.metadata || ({} as MetaDataInterface);
  const modelSig = models
    .map((m) => `${m.name}|${m.position?.x ?? 0},${m.position?.y ?? 0}`)
    .join(";");
  const taskSig = `${tasks.length}`;
  const metaSig = JSON.stringify(metadata);
  return `${modelSig}#${taskSig}#${metaSig}`;
};

export const randomizeArrays = <T,>(arrays: T[]): T[] => {
  return arrays.sort(() => Math.random() - 0.5);
};

// API wrappers
export const extractSRS = async (
  srs: string
): Promise<{ tasks: TaskInterface[]; models: ModelInterface[]; metadata: MetaDataInterface; backendState?: import('@/interfaces/codeeditingservice/ConfigInterfaces').BackendState }> => {
  const response = await fetch("/api/extract", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ srs }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to extract SRS: ${errorText}`);
  }

  const result = await response.json();

  // Defensive: ensure result.data exists and has the right shape
  if (!result?.data) {
    throw new Error("Malformed response from /api/extract");
  }

  const { tasks, models, metadata, backendState } = result.data;

  return {
    tasks: tasks as TaskInterface[] ?? [],
    models: models as ModelInterface[] ?? [],
    metadata: metadata as MetaDataInterface ?? {},
    backendState: backendState as import('@/interfaces/codeeditingservice/ConfigInterfaces').BackendState | undefined,
  };
};

export const generateCodeZip = async (
  payload: { models: ModelInterface[]; metaData: MetaDataInterface; tasks: TaskInterface[] }
): Promise<Blob> => {
  const response = await fetch("/api/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error("Failed to generate code");
  return await response.blob();
};

// Position generation helper for models when they lack positions
export const ensureModelPositions = (models: ModelInterface[]): { [k: string]: { x: number; y: number } } => {
  const hasPositions = models.some((m) => m.position);
  if (hasPositions) {
    const positions: { [key: string]: { x: number; y: number } } = {};
    models.forEach((m) => {
      if (m.position) positions[m.name] = m.position;
    });
    return positions;
  }
  const schema = generateConstellationSchema(models);
  return getConstellationPositions(schema);
};


export const loadJsonModule = (id: string) => {
  try {
    const importedModule = (AllModels as Record<string, any>)[id];
    if (!importedModule) {
      throw new Error(`Key "${id}" not found in AllModels.`);
    }

    const testModels: ModelInterface[] = importedModule?.default || importedModule;
    const testMetaData: MetaDataInterface = {
      rateLimiterEnabled: false,
      corsEnabled: false,
      storageType: StorageType.AWS_S3,
      websocketEnabled: false,
      authentication: true,
      authenticationMethods: { emailPassword: true, googleAuth: true },
      fileStorage: false,
      databaseType: DatabaseType.POSTGRESQL,
    };

    return { models: testModels, metadata: testMetaData };
  } catch (error) {
    throw new Error(
      `Invalid schema key: ${id}. Details: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
};

export const calculateNewNodePositions = (
  newModels: ModelInterface[],
  existingNodes: ModelNodeInterface[]
): { [key: string]: { x: number; y: number } } => {
  const positions: { [key: string]: { x: number; y: number } } = {};
  
  // Calculate bounding box of existing nodes
  let maxX = 0;
  let minY = 0;
  let maxY = 0;
  
  if (existingNodes.length > 0) {
    existingNodes.forEach(node => {
      if (node.position.x > maxX) maxX = node.position.x;
      if (node.position.y < minY) minY = node.position.y;
      if (node.position.y > maxY) maxY = node.position.y;
    });
    // Add padding
    maxX += 500; 
  } else {
    // Start from default if no nodes
    maxX = 100;
    minY = 100;
  }

  // Use Constellation Schema Algo to determine relative positions
  const schema = generateConstellationSchema(newModels);
  
  // Calculate center Y for the new cluster (align with middle of existing nodes or default)
  const centerY = existingNodes.length > 0 ? (minY + maxY) / 2 : 300;
  
  // Get relative positions from the algo
  // Pass maxX + offset as the center X for the new cluster
  // We estimate the width of the new cluster to center it properly relative to maxX
  const estimatedWidth = Math.max(400, 650 * schema.factTables.length);
  const newCenterX = maxX + (estimatedWidth / 2);

  const constellationPositions = getConstellationPositions(schema, newCenterX, centerY);

  // Map the positions back to model names
  newModels.forEach((model) => {
    if (constellationPositions[model.name]) {
      positions[model.name] = constellationPositions[model.name];
    } else {
      // Fallback for any model not covered by the algo (shouldn't happen if algo is correct)
      positions[model.name] = { x: maxX, y: centerY };
    }
  });

  return positions;
};